/**
 * Error Handling Utilities
 * Provides consistent error handling across all services
 */

/**
 * Custom error class for service errors
 */
export class ServiceError extends Error {
  constructor(message, status = null, originalError = null) {
    super(message);
    this.name = 'ServiceError';
    this.status = status;
    this.originalError = originalError;
    this.timestamp = new Date().toISOString();
  }
}

/**
 * Safe fetch wrapper with consistent error handling
 * @param {string} url - URL to fetch
 * @param {object} options - Fetch options
 * @returns {Promise<Response>} - Fetch response
 * @throws {ServiceError} - On fetch failure
 */
export async function safeFetch(url, options = {}) {
  try {
    const response = await fetch(url, options);

    if (!response.ok) {
      throw new ServiceError(
        `HTTP ${response.status}: ${response.statusText}`,
        response.status
      );
    }

    return response;
  } catch (error) {
    if (error instanceof ServiceError) {
      throw error;
    }

    // Network error or other fetch failure
    throw new ServiceError(
      `Failed to fetch ${url}: ${error.message}`,
      null,
      error
    );
  }
}

/**
 * Structured error logging
 * @param {string} context - Context where error occurred (e.g., 'contentService.getPost')
 * @param {Error} error - Error object
 * @param {object} metadata - Additional metadata
 */
export function logError(context, error, metadata = {}) {
  const errorLog = {
    context,
    message: error.message,
    name: error.name,
    timestamp: new Date().toISOString(),
    ...metadata
  };

  if (error instanceof ServiceError) {
    errorLog.status = error.status;
    errorLog.originalError = error.originalError?.message;
  }

  if (error.stack) {
    errorLog.stack = error.stack;
  }

  // Log to console in development, could be sent to error tracking service in production
  console.error(`[${context}]`, errorLog);

  // TODO: In production, send to error tracking service (e.g., Sentry)
  // if (process.env.NODE_ENV === 'production') {
  //   sendToErrorTracker(errorLog);
  // }
}
